import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { useContent } from "../../i18n/content";
import { SectionHeader } from "../SectionHeader";
import { Reveal } from "../motion/Reveal";

export const Companies = () => {
    const { ui, companies } = useContent();
    return (
    <section id="companies" data-testid="companies-section" className="relative py-24 lg:py-32 bg-[#24251f]">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-12">
            <SectionHeader
                chapter={ui.companies.chapter}
                overline={ui.companies.overline}
                title={ui.companies.title}
                description={ui.companies.description}
            />

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8">
                {companies.map((c, i) => (
                    <Reveal key={c.slug} delay={0.12 * i} className="h-full">
                        <Link
                            to={c.route}
                            data-testid={`company-card-${c.slug}`}
                            className="group relative flex flex-col h-full border border-[#4a4c42] bg-[#292a24] overflow-hidden hover:border-[#f6c422]/70 transition-colors duration-500"
                        >
                            <div className="relative h-56 sm:h-64 overflow-hidden bg-[#f4f4f0]">
                                <img
                                    src={c.cardImage || c.image}
                                    alt={`${c.fullName} — ${c.category}`}
                                    className="w-full h-full object-cover transition-transform duration-[1200ms] ease-out group-hover:scale-105"
                                    loading="lazy"
                                />
                                <span className="absolute top-5 left-5 font-display text-[10px] tracking-[0.28em] uppercase text-[#f6c422] bg-[#292a24]/85 backdrop-blur-sm px-3 py-2 border border-[#f6c422]/30">
                                    {c.category}
                                </span>
                                {c.logo && (
                                    <span className="absolute inset-0 flex items-center justify-center p-10">
                                        <img
                                            src={c.logo}
                                            alt={`${c.cardTitle} logo`}
                                            className="max-h-24 w-auto max-w-[75%] object-contain drop-shadow-[0_2px_14px_rgba(255,255,255,0.5)]"
                                            loading="lazy"
                                        />
                                    </span>
                                )}
                            </div>

                            <div className="flex flex-col flex-1 p-7">
                                <p className="overline-text !text-[10px]">{String(i + 1).padStart(2, "0")} — {c.tagline}</p>
                                <h3 className="font-display text-2xl font-extrabold tracking-wide text-white uppercase mt-3">
                                    {c.cardTitle}
                                </h3>
                                <p className="text-xs font-display font-semibold tracking-[0.16em] text-[#a2a59d] uppercase mt-1">
                                    {c.fullName}
                                </p>
                                <p className="text-sm text-[#a2a59d] leading-relaxed mt-4 flex-1">
                                    {c.description}
                                </p>

                                <ul className="mt-6 space-y-2">
                                    {c.cardCapabilities.slice(0, 5).map((cap) => (
                                        <li key={cap} className="flex items-center gap-3 text-xs text-white/70">
                                            <span className="w-1.5 h-1.5 rotate-45 bg-[#f6c422]/70 shrink-0" />
                                            {cap}
                                        </li>
                                    ))}
                                    {c.cardCapabilities.length > 5 && (
                                        <li className="text-xs text-[#a2a59d]/60 pl-[18px]">
                                            +{c.cardCapabilities.length - 5} {ui.companies.more}
                                        </li>
                                    )}
                                </ul>

                                <span
                                    data-testid={`company-card-cta-${c.slug}`}
                                    className="mt-8 inline-flex items-center gap-2 font-display font-bold text-[11px] tracking-[0.2em] uppercase text-[#f6c422] group-hover:gap-4 transition-[gap] duration-300"
                                >
                                    {ui.companies.explore} {c.slug === "sadja-group" ? "Sadja Group" : c.cardTitle.charAt(0) + c.cardTitle.slice(1).toLowerCase()}
                                    <ArrowUpRight className="w-4 h-4" />
                                </span>
                            </div>
                        </Link>
                    </Reveal>
                ))}
            </div>
        </div>
    </section>
    );
};
